import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, ArrowRight, Check, Info } from 'lucide-react'
import BrandLogo from './BrandLogo'
import {
  SECTION_TUTORIALS,
  type SectionId,
  type TutorialStep,
} from '../lib/sectionTutorialContent'

const BG = '#0A0A0A'

/* ── Reusable primitives ───────────────────────────────────────────── */

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm ${className}`}>
      {children}
    </div>
  )
}

function StepBadge({ number, active, completed }: { number: number; active: boolean; completed: boolean }) {
  if (completed) {
    return (
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
        <Check className="h-3.5 w-3.5 text-emerald-400" />
      </div>
    )
  }
  return (
    <div
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
        active ? 'bg-[#FF2300]/20 text-[#FF2300]' : 'bg-white/[0.06] text-zinc-500'
      }`}
    >
      {number}
    </div>
  )
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = ((current + 1) / total) * 100
  return (
    <div className="relative h-[3px] w-full overflow-hidden rounded-full">
      <div className="absolute inset-0 bg-white/[0.06]" />
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
        style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #FF230080, #FF2300)' }}
      />
    </div>
  )
}

/* ── Step content ──────────────────────────────────────────────────── */

function StepContent({ step, stepIndex, totalSteps, sectionColor }: { step: TutorialStep; stepIndex: number; totalSteps: number; sectionColor: string }) {
  const Icon = step.icon
  return (
    <div className="space-y-5">
      <div>
        <span className="inline-block text-[11px] font-semibold uppercase tracking-widest" style={{ color: sectionColor }}>
          {stepIndex + 1} of {totalSteps}
        </span>
        <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-[1.12]">
          {step.title}
        </h1>
        <p className="mt-3 text-[13px] text-zinc-400 leading-relaxed max-w-[480px]">
          {step.description}
        </p>
      </div>

      <div className="h-[1px] w-12 bg-white/[0.08]" />

      {step.tips && step.tips.length > 0 && (
        <div className="space-y-3">
          {step.tips.map((tip, i) => (
            <GlassCard key={i} className="p-4">
              <div className="flex gap-3.5">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${step.iconColor}15` }}
                >
                  {i === 0 ? (
                    <Icon className="h-4 w-4" style={{ color: step.iconColor }} />
                  ) : (
                    <span className="text-[13px] font-bold" style={{ color: step.iconColor }}>{i + 1}</span>
                  )}
                </div>
                <div>
                  <p className="text-[13px] text-zinc-300 leading-relaxed">{tip}</p>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Step navigator (sidebar mini-map) ─────────────────────────────── */

function StepNav({ steps, current, setCurrent }: { steps: TutorialStep[]; current: number; setCurrent: (i: number) => void }) {
  return (
    <div className="space-y-1.5">
      {steps.map((step, i) => (
        <button
          key={i}
          type="button"
          onClick={() => setCurrent(i)}
          className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
            i === current ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
          }`}
        >
          <StepBadge number={i + 1} active={i === current} completed={i < current} />
          <span className={`text-[12px] ${i === current ? 'text-white font-medium' : i < current ? 'text-zinc-400' : 'text-zinc-600'}`}>
            {step.title}
          </span>
        </button>
      ))}
    </div>
  )
}

/* ── Tutorial overlay ──────────────────────────────────────────────── */

interface SectionTutorialOverlayProps {
  sectionId: SectionId
  isOpen: boolean
  onClose: () => void
}

function SectionTutorialOverlay({ sectionId, isOpen, onClose }: SectionTutorialOverlayProps) {
  const [step, setStep] = useState(0)
  const [entering, setEntering] = useState(false)

  const tutorial = SECTION_TUTORIALS[sectionId]
  const totalSteps = tutorial.steps.length
  const SectionIcon = tutorial.sectionIcon

  useEffect(() => {
    if (isOpen) {
      setStep(0)
      setEntering(true)
      const id = window.setTimeout(() => setEntering(false), 50)
      return () => window.clearTimeout(id)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && step < totalSteps - 1) setStep((s) => s + 1)
      if (e.key === 'ArrowLeft' && step > 0) setStep((s) => s - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose, step, totalSteps])

  if (!isOpen) return null

  const overlay = (
    <div
      className="fixed inset-0 z-[120] flex flex-col select-none"
      style={{ backgroundColor: BG, transition: 'opacity 0.4s ease', opacity: entering ? 0 : 1 }}
      role="dialog"
      aria-modal="true"
      aria-label={`${tutorial.sectionTitle} tutorial`}
    >
      {/* Background glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[500px] h-[500px] rounded-full opacity-[0.04] blur-[120px]"
          style={{ background: `radial-gradient(circle, ${tutorial.sectionColor} 0%, transparent 70%)`, top: '-15%', right: '-8%' }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full opacity-[0.03] blur-[100px]"
          style={{ background: `radial-gradient(circle, ${tutorial.sectionColor} 0%, transparent 70%)`, bottom: '-15%', left: '-8%' }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10 w-full shrink-0 px-4 sm:px-8 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="mx-auto flex w-full max-w-[960px] items-center gap-5 py-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-4 h-4 rounded-sm" style={{ background: tutorial.sectionColor }} />
            <BrandLogo className="h-2.5 w-auto text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <ProgressBar current={step} total={totalSteps} />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[11px] text-zinc-500 font-medium hidden sm:inline">
              {step + 1} of {totalSteps}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="text-[11px] text-zinc-500 hover:text-white transition-colors font-medium tracking-wide uppercase"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 min-h-0 flex">
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide px-4 sm:px-8">
          <div className="mx-auto w-full max-w-[960px] flex gap-8 py-4 sm:py-6">
            {/* Step navigator — desktop only */}
            <div className="hidden lg:block w-[200px] shrink-0 sticky top-0 self-start pt-2">
              {/* Section badge */}
              <div className="flex items-center gap-2.5 mb-6 px-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${tutorial.sectionColor}20` }}
                >
                  <SectionIcon className="h-4 w-4" style={{ color: tutorial.sectionColor }} />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-white">{tutorial.sectionTitle}</p>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Guide</p>
                </div>
              </div>
              <StepNav steps={tutorial.steps} current={step} setCurrent={setStep} />
            </div>

            {/* Intro + step content */}
            <div className="flex-1 min-w-0 pb-8">
              {step === 0 && (
                <div className="mb-8 tut-slide-enter">
                  <GlassCard className="p-5">
                    <div className="flex gap-4">
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${tutorial.sectionColor}20` }}
                      >
                        <SectionIcon className="h-5 w-5" style={{ color: tutorial.sectionColor }} />
                      </div>
                      <div>
                        <p className="text-[15px] font-semibold text-white">{tutorial.sectionTitle}</p>
                        <p className="mt-1 text-[13px] text-zinc-400 leading-relaxed">{tutorial.intro}</p>
                      </div>
                    </div>
                  </GlassCard>
                </div>
              )}
              <div key={step} className="tut-slide-enter">
                <StepContent
                  step={tutorial.steps[step]}
                  stepIndex={step}
                  totalSteps={totalSteps}
                  sectionColor={tutorial.sectionColor}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer nav */}
      <div className="relative z-10 shrink-0 px-4 sm:px-8 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex w-full max-w-[960px] items-center justify-between border-t border-white/[0.06] pt-4 pb-1">
          <div className="w-16">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-white transition-colors font-medium"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
          </div>

          {/* Dot indicators */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? 'w-6' : 'w-1.5 hover:opacity-80'}`}
                style={{
                  background: i === step
                    ? tutorial.sectionColor
                    : i < step
                      ? `${tutorial.sectionColor}4D`
                      : 'rgba(255,255,255,0.15)',
                }}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>

          {step < totalSteps - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[12px] font-semibold text-white transition-all"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              Continue <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[12px] font-semibold text-white transition-all"
              style={{ background: tutorial.sectionColor, boxShadow: `0 0 24px ${tutorial.sectionColor}33` }}
            >
              Got it <Check className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <style>{`
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        @keyframes tutSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .tut-slide-enter {
          animation: tutSlideIn 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        }
      `}</style>
    </div>
  )

  return typeof document === 'undefined' ? null : createPortal(overlay, document.body)
}

/* ── Info button (the "i" icon) ────────────────────────────────────── */

interface SectionInfoButtonProps {
  sectionId: SectionId
  className?: string
}

export default function SectionInfoButton({ sectionId, className = '' }: SectionInfoButtonProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 dark:border-zinc-600 text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 hover:border-gray-400 dark:hover:border-zinc-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all ${className}`}
        aria-label={`Learn about ${SECTION_TUTORIALS[sectionId].sectionTitle}`}
        title={`Learn about ${SECTION_TUTORIALS[sectionId].sectionTitle}`}
      >
        <Info className="w-3 h-3" />
      </button>
      <SectionTutorialOverlay
        sectionId={sectionId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  )
}
